/**
 * 处理Markdown中的块级元素的完整性（标题、引用、代码块等）
 */
import { LogDebugFunction } from '../utils/markdownTypes';

/**
 * 处理标题元素
 */
export const processHeading = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 标题处理 - 确保 # 后面加上空格和至少一个字符
  if (/#+ *$/.test(analyzedText)) {
    const headerMatch = remainingText.match(/^(#+)( +)([^ \n])?/);
    if (headerMatch) {
      // 计算至少需要显示的字符：# + 空格 + 至少一个内容字符
      const hashLen = headerMatch[1]?.length || 0;
      const spaceLen = headerMatch[2]?.length || 0;
      const minRequired = hashLen + spaceLen + 1; // +1 表示至少一个内容字符
      
      if (charsToShow < minRequired) {
        return logDebugInfo('标题', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理引用块
 */
export const processBlockquote = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 引用处理
  if (/> *$/.test(analyzedText)) {
    const quoteMatch = remainingText.match(/^(>\s+)([^ \n])?/);
    if (quoteMatch) {
      const gtWithSpace = quoteMatch[1]?.length || 0;
      const minRequired = gtWithSpace + 1; // +1 表示至少一个内容字符
      
      if (charsToShow < minRequired) {
        return logDebugInfo('引用', minRequired - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理代码块
 */
export const processCodeBlock = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 代码块处理
  if (/```[^`]*$/.test(analyzedText)) {
    // 代码块开始后，尝试找到整行或至少一个描述语言的词
    const codeBlockMatch = remainingText.match(/^```([^\n]*)?(\n|$)/);
    if (codeBlockMatch) {
      const firstLine = codeBlockMatch[0].length;
      if (charsToShow < firstLine) {
        return logDebugInfo('代码块', firstLine - charsToShow);
      }
    }
  }
  
  return 0;
};

/**
 * 处理内联代码
 */
export const processInlineCode = (
  text: string, 
  startIdx: number, 
  charsToShow: number, 
  analyzedText: string,
  remainingText: string,
  logDebugInfo: LogDebugFunction
): number => {
  // 内联代码处理
  if (/`[^`]*$/.test(analyzedText) && !/```[^`]*$/.test(analyzedText)) {
    const inlineCodeMatch = remainingText.match(/^`([^`\n]*?)(`)?/);
    if (inlineCodeMatch) {
      // 如果已经找到闭合的 `，则需要整个内联代码一次显示
      if (inlineCodeMatch[2]) {
        const fullCode = inlineCodeMatch[0].length;
        if (charsToShow < fullCode) {
          return logDebugInfo('内联代码-完整', fullCode - charsToShow);
        }
      } else {
        // 否则至少要显示 ` 加一个字符
        const minRequired = 2; // ` + 至少一个字符
        if (charsToShow < minRequired) {
          return logDebugInfo('内联代码-前缀', minRequired - charsToShow);
        }
      }
    }
  }
  
  return 0;
}; 